import { useState, useEffect } from "react";
import Select from "react-select";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Filter, RotateCcw } from "lucide-react";
import Campo from "../posgrados/Campo";
import Modalidad from "../posgrados/Modalidad";
import DataFetcherOfertaPresencial from "../../hooks/DataFetcherOfertaPresencial";

function OfferLinea() {
  const optionsFacultad = [
    { value: "todos", label: "Todas las facultades" },
    {
      value: "Facultad de Ciencias Económicas y Empresariales",
      label: "Ciencias Económicas y Empresariales",
    },
    {
      value: "Facultad de Ciencias del Comportamiento",
      label: "Ciencias del Comportamiento",
    },
    {
      value: "Facultad de Ciencias Exactas y Naturales",
      label: "Ciencias Exactas y Naturales",
    },
    {
      value: "Facultad de Ciencias Sociales, Educación y Humanidades",
      label: "Ciencias Sociales, Educación y Humanidades",
    },
    {
      value: "Facultad de Ciencias Jurídicas y Políticas",
      label: "Ciencias Jurídicas y Políticas",
    },
    {
      value: "Facultad de Ingenierías y Arquitectura",
      label: "Ingenierías y Arquitectura",
    },
  ];

  const cleanText = (text) => {
    if (!text) return "";

    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  const getInitialValue = (param, options) => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(param);

    return options.find((o) => o.value === value) || options[0];
  };

  const [facultadSeleccionada, setFacultadSeleccionada] = useState(
    getInitialValue("facultad", optionsFacultad),
  );

  // Actualizar URL
  useEffect(() => {
    const params = new URLSearchParams();

    if (facultadSeleccionada.value !== "todos") {
      params.set("facultad", facultadSeleccionada.value);
    }

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");

    window.history.replaceState({}, "", newUrl);
  }, [facultadSeleccionada]);

  const limpiarFiltros = () => {
    setFacultadSeleccionada(optionsFacultad[0]);
  };

  const DataDisplayOfertaPosgrados = ({ data }) => {
    const badgeColors = {
      Maestría: "bg-blue-100 text-blue-800 border border-blue-200",
      Especialización: "bg-purple-100 text-purple-800 border border-purple-200",
      Doctorado: "bg-emerald-100 text-emerald-800 border border-emerald-200",
    };

    // FILTRAR POR FACULTAD
    const filtered = data.filter((item) => {
      if (facultadSeleccionada.value === "todos") {
        return true;
      }

      if (!item.field_facultad) {
        return false;
      }

      const facultadItem = cleanText(item.field_facultad);
      const facultadSeleccionadaLimpia = cleanText(facultadSeleccionada.value);

      return (
        facultadItem === facultadSeleccionadaLimpia ||
        facultadItem.includes(facultadSeleccionadaLimpia) ||
        facultadSeleccionadaLimpia.includes(facultadItem)
      );
    });

    // ORDENAR LOS DATOS
    const filteredData = [...filtered].sort((a, b) => {
      const dateA = a.field_etiqueta_enlinea || "";
      const dateB = b.field_etiqueta_enlinea || "";

      const dateComparison = dateB.localeCompare(dateA, "es");

      if (dateComparison !== 0) {
        return dateComparison;
      }

      const titleA = cleanText(a.title);
      const titleB = cleanText(b.title);

      return titleA.localeCompare(titleB, "es");
    });

    return (
      <div className="px-16 md:px-10">
        {/* FILTROS */}
        <div className="wrapper-filtros layout9">
          <div>
            <p>
              <b>Facultad</b>
            </p>

            <Select
              options={optionsFacultad}
              value={facultadSeleccionada}
              onChange={setFacultadSeleccionada}
              isSearchable
              placeholder="Selecciona una facultad"
            />
          </div>

          <div>
            <button
              className="btn-reset-filtros flex items-center gap-2"
              onClick={limpiarFiltros}
            >
              <RotateCcw size={16} />
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* CONTADOR */}
        <div className="mb-6 flex items-center justify-between">
          <p className="flex items-center gap-2 text-gray-700">
            <Filter size={16} />
            <span className="font-bold">{filteredData.length}</span>
            programas encontrados
          </p>
        </div>

        {/* GRID */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {filteredData.map((item) => (
            <motion.a
              key={item.id}
              href={item.field_url}
              rel="noreferrer"
              className="group relative block overflow-hidden rounded-2xl bg-white shadow-md"
              initial="rest"
              animate="rest"
              whileHover="hover"
            >
              {/* IMAGEN */}
              <div className="h-40 overflow-hidden">
                <img
                  src={item.field_imagen_portada}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              </div>

              {/* ICONO */}
              <motion.div
                variants={{
                  rest: {
                    scale: 1,
                    opacity: 0.8,
                  },
                  hover: {
                    scale: 1.2,
                    opacity: 1,
                  },
                }}
                className="absolute right-4 top-4 rounded-full bg-white/90 p-2 shadow-md backdrop-blur-md"
              >
                <Plus size={12} />
              </motion.div>

              {/* CONTENIDO */}
              <div className="space-y-3 p-5">
                {/* ETIQUETA */}
                {item.field_etiqueta_enlinea && (
                  <div className="flex flex-wrap gap-2">
                    <span
                      className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                        badgeColors[item.field_programa] ||
                        "border border-gray-200 bg-slate-100 text-[#004270]"
                      }`}
                    >
                      {item.field_etiqueta_enlinea}
                    </span>
                  </div>
                )}

                {item.prueba && (
                  <>
                    <p> {item.prueba}</p>
                  </>
                )}

                {/* TÍTULO */}
                <h3 className="p-0 text-base font-bold leading-tight">
                  {item.title}
                </h3>

                {/* MODALIDAD */}
                {item.field_modalidad && (
                  <div
                    className="
                      [&_p]:inline-flex
                      [&_p]:p-0
                      [&_p]:text-sm
                      [&_p]:leading-none
                      [&_p]:text-gray-600
                      [&_ul]:m-0
                      [&_ul]:p-0
                      [&_li]:py-0
                      [&_li]:pt-1
                      [&_li]:text-sm
                      [&_li]:leading-none
                      [&_li]:text-gray-600
                    "
                  >
                    <p>
                      <strong>Modalidad: </strong>
                    </p>

                    <Modalidad value={item.field_modalidad} />
                  </div>
                )}

                {/* DURACIÓN */}
                {item.field_duracion && (
                  <p className="p-0 text-sm leading-none text-gray-600">
                    <strong>Duración:</strong>{" "}
                    {item.field_duracion
                      .replaceAll("_", " ")
                      .replace("anos", "años")
                      .replace("ano", "año")}
                  </p>
                )}

                {/* FACULTAD */}
                {item.field_facultad && (
                  <p className="p-0 text-sm leading-none text-gray-600">
                    <strong>Facultad:</strong> {item.field_facultad}
                  </p>
                )}

                {/* CAMPO */}
                {item.field_campo_amplio && (
                  <p className="p-0 text-sm leading-none text-gray-600">
                    <strong>Campo: </strong>

                    <Campo value={item.field_campo_amplio} />
                  </p>
                )}

                {/* INICIO DE CLASES */}
                {item.field_inicio_clases && (
                  <p className="p-0 text-sm leading-none text-gray-600">
                    <strong>Inicio de clases:</strong>{" "}
                    {item.field_inicio_clases}
                  </p>
                )}
              </div>

              {/* OVERLAY */}
              <motion.div
                variants={{
                  rest: {
                    opacity: 0,
                  },
                  hover: {
                    opacity: 1,
                  },
                }}
                className="absolute inset-0 flex items-center justify-center bg-black/10"
              >
                <ArrowRight size={44} className="text-white" />
              </motion.div>
            </motion.a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DataFetcherOfertaPresencial>
      {(data) => <DataDisplayOfertaPosgrados data={data} />}
    </DataFetcherOfertaPresencial>
  );
}

export default OfferLinea;
